<template>
  <q-dialog
    :model-value="modelValue"
    :maximized="$q.screen.lt.sm"
    no-route-dismiss
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card v-if="oportunidade" class="neg-escopo neg-detalhe" :class="{ 'neg-detalhe--cheio': $q.screen.lt.sm }">
      <q-card-section class="neg-drawer__topo row items-center no-wrap">
        <div class="neg-drawer__identificacao">
          <div class="text-h6 ellipsis">{{ oportunidade.codigo }} · {{ nomeDoCliente }}</div>
          <div class="text-caption text-grey-7">{{ subtitulo }}</div>
        </div>
        <q-badge
          v-if="alerta"
          :color="corDoAlertaNegocios(oportunidade.alerta_nivel)"
          :text-color="textoDoAlertaNegocios(oportunidade.alerta_nivel)"
        >{{ alerta }}</q-badge>
        <q-btn flat round dense icon="close" @click="$emit('update:modelValue', false)" />
      </q-card-section>

      <q-separator />

      <div class="neg-detalhe__colunas" :class="{ 'neg-detalhe__colunas--lado-a-lado': !telaEstreita }">
        <div class="neg-detalhe__dados">
          <div class="neg-secao neg-secao--primeira">Movimentar</div>
          <div class="row items-center q-gutter-sm">
            <q-chip
              v-for="etapa in negociosEtapas"
              :key="etapa.id"
              clickable
              :disable="!podeEditarOportunidade(oportunidade)"
              :color="etapa.id === oportunidade.etapa_id ? 'secondary' : 'grey-3'"
              :text-color="etapa.id === oportunidade.etapa_id ? 'white' : 'grey-8'"
              @click="$emit('mover', etapa)"
            >
              {{ etapa.nome }}
            </q-chip>
          </div>
          <div class="neg-sub">{{ avisoDoPrazo }}</div>

          <div class="neg-secao">Dados</div>
          <div class="neg-linha">
            <span>Etapa</span>
            <span>{{ nomeDaEtapa }}</span>
          </div>
          <div v-if="listaDeLinhas.length <= 1" class="neg-linha">
            <span>Linha</span>
            <span>{{ oportunidade.linha ? oportunidade.linha.nome : "—" }}</span>
          </div>
          <template v-else>
            <div class="neg-secao">Linhas contratadas</div>
            <div v-for="linha in listaDeLinhas" :key="linha.id" class="neg-linha">
              <span>
                {{ linha.nome }}
                <div v-if="detalheDaLinha(linha)" class="neg-sub">{{ detalheDaLinha(linha) }}</div>
              </span>
              <span>
                {{ linha.valor_contratado === null || linha.valor_contratado === undefined
                  ? "—"
                  : formatarMoeda(linha.valor_contratado) }}
              </span>
            </div>
          </template>
          <div class="neg-linha">
            <span>Valor solicitado</span>
            <span>{{ oportunidade.valor_solicitado === null ? "—" : formatarMoeda(oportunidade.valor_solicitado) }}</span>
          </div>
          <div class="neg-linha">
            <span>Valor contratado</span>
            <span>{{ oportunidade.valor_contratado === null ? "—" : formatarMoeda(oportunidade.valor_contratado) }}</span>
          </div>
          <div class="neg-linha">
            <span>Prazo</span>
            <span>{{ oportunidade.prazo ? oportunidade.prazo + " parcelas" : "—" }}</span>
          </div>
          <div class="neg-linha">
            <span>Valor da parcela</span>
            <span>{{ oportunidade.valor_parcela === null ? "—" : formatarMoeda(oportunidade.valor_parcela) }}</span>
          </div>
          <div class="neg-linha">
            <span>Taxa</span>
            <span>{{ oportunidade.taxa === null ? "—" : formatarPercentual(oportunidade.taxa) }}</span>
          </div>
          <div class="neg-linha">
            <span>Protocolo</span>
            <span>{{ oportunidade.protocolo || "—" }}</span>
          </div>
          <div class="neg-linha">
            <span>Data de retorno</span>
            <span>
              {{ dataParaTela(oportunidade.data_alerta) || "—" }}
              <q-btn
                v-if="podeEditarOportunidade(oportunidade)"
                flat
                dense
                size="sm"
                icon="edit_calendar"
                @click="$emit('alterar-data')"
              >
                <q-tooltip>Alterar a data de retorno</q-tooltip>
              </q-btn>
            </span>
          </div>
          <div class="neg-linha">
            <span>Operador</span>
            <span>{{ oportunidade.operador || "—" }}</span>
          </div>
          <div class="neg-linha">
            <span>Aberta em</span>
            <span>{{ $formatDateTime(oportunidade.created) }}</span>
          </div>

          <div v-if="checklist" class="neg-secao neg-secao--checklist">
            <span>Checklist de documentos</span>
            <span class="neg-checklist__contagem">{{ checklist.marcados }}/{{ checklist.total }}</span>
          </div>
          <div v-if="checklist" class="neg-checklist">
            <button
              v-for="item in checklist.itens"
              :key="item.item"
              type="button"
              class="neg-checklist__item"
              :class="{ 'neg-checklist__item--on': item.marcado }"
              :disabled="!podeAlterarChecklist"
              @click="alternarChecklistItem(item)"
            >
              <q-icon :name="item.marcado ? 'check_box' : 'check_box_outline_blank'" size="20px" />
              {{ item.rotulo }}
            </button>
          </div>
          <div v-if="checklist && checklist.somente_leitura" class="neg-sub">
            Esta oportunidade está em uma etapa que encerra o fluxo. O checklist fica só para leitura.
          </div>

          <div class="neg-secao">Comissão</div>
          <template v-if="comissaoVisivel(oportunidade)">
            <div class="neg-linha">
              <span>Percentual previsto</span>
              <span>{{ formatarPercentual(oportunidade.percentual_previsto) || "—" }}</span>
            </div>
            <div class="neg-linha">
              <span>Comissão prevista</span>
              <span>{{ formatarMoeda(oportunidade.comissao_prevista) }}</span>
            </div>
            <div class="neg-linha">
              <span>Comissão recebida</span>
              <span>{{ formatarMoeda(oportunidade.comissao_recebida) }}</span>
            </div>
            <div class="neg-linha">
              <span>Recebimento previsto</span>
              <span>{{ dataParaTela(oportunidade.data_prevista_recebimento) || "—" }}</span>
            </div>
          </template>
          <div v-else class="neg-sub neg-oculto">
            •••• Sem permissão para ver a comissão de oportunidade de outro operador
          </div>

          <template v-if="comissaoVisivel(oportunidade)">
            <div class="neg-secao">Recebimentos</div>
            <div v-if="!oportunidade.recebimentos.length" class="neg-sub">
              Nenhum recebimento registrado
            </div>
            <div
              v-for="recebimento in oportunidade.recebimentos"
              :key="recebimento.id"
              class="neg-linha"
            >
              <span>
                {{ dataParaTela(recebimento.data) }}
                <div v-if="recebimento.observacao" class="neg-sub">{{ recebimento.observacao }}</div>
                <div v-if="recebimento.nota_fiscal" class="neg-sub neg-recebimento__nota">
                  Nota fiscal: {{ recebimento.nota_fiscal.status_nome
                  }}<template v-if="recebimento.nota_fiscal.numero_nfse">
                    · Nº {{ recebimento.nota_fiscal.numero_nfse }}</template
                  ><template v-if="recebimento.nota_fiscal.observacao">
                    · {{ recebimento.nota_fiscal.observacao }}</template
                  >
                </div>
              </span>
              <span>
                <span :class="{ 'neg-negativo': recebimento.valor < 0 }">
                  {{ formatarMoeda(recebimento.valor) }}
                </span>
                <q-btn
                  v-if="podeEditarOportunidade(oportunidade) && podeEmitirNotaFiscal(recebimento)"
                  flat
                  dense
                  size="sm"
                  icon="receipt_long"
                  class="neg-recebimento__emitir"
                  @click="$emit('emitir-nota-fiscal', recebimento)"
                >
                  <q-tooltip>Emitir nota fiscal deste repasse</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="podeEditarOportunidade(oportunidade) && podeVerComissaoNegocios"
                  flat
                  dense
                  size="sm"
                  icon="edit"
                  class="neg-recebimento__editar"
                  @click="$emit('editar-recebimento', recebimento)"
                >
                  <q-tooltip>Corrigir este recebimento</q-tooltip>
                </q-btn>
              </span>
            </div>
          </template>

        </div>

        <div class="neg-detalhe__historico">
          <div class="neg-secao neg-secao--primeira">Histórico</div>
          <div v-if="podeEditarOportunidade(oportunidade)" class="neg-anotacao-form">
            <q-input
              v-model="textoAnotacao"
              type="textarea"
              autogrow
              maxlength="10000"
              label="Nova anotação"
              placeholder="Documento recebido, retorno do banco, combinado com o cliente..."
              :disable="salvandoAnotacao"
              :error="Boolean(erroAnotacao)"
              :error-message="erroAnotacao"
              hide-bottom-space
            />
            <div class="neg-anotacao-form__acoes">
              <q-btn
                unelevated
                no-caps
                color="primary"
                icon="add_comment"
                label="Registrar anotação"
                :loading="salvandoAnotacao"
                :disable="salvandoAnotacao"
                @click="registrarAnotacao"
              />
            </div>
          </div>
          <div v-if="!historicoRecentePrimeiro.length" class="neg-sub">Nenhum registro no histórico ainda.</div>
          <q-timeline v-else color="primary" layout="dense" class="neg-timeline">
            <q-timeline-entry
              v-for="registro in historicoRecentePrimeiro"
              :key="registro.id"
              class="neg-historico"
              :icon="iconeDoRegistro(registro)"
            >
              <template #title>
                <span class="neg-historico__titulo">{{ tituloDoRegistro(registro) }}</span>
              </template>
              <template #subtitle>
                {{ registro.nome || "Sistema" }} · {{ $formatDateTime(registro.created) }}
              </template>
              <div v-if="registro.tipo === 'anotacao'" class="neg-anotacao-texto">{{ registro.motivo }}</div>
              <div v-else-if="registro.motivo" class="neg-sub">{{ registro.motivo }}</div>
            </q-timeline-entry>
          </q-timeline>
        </div>
      </div>

      <q-separator />

      <q-card-actions class="neg-drawer__rodape">
        <q-btn flat no-caps label="Fechar" @click="$emit('update:modelValue', false)" />
        <q-space />
        <q-btn
          v-if="podeExcluirNegocios && podeEditarOportunidade(oportunidade)"
          flat
          no-caps
          color="negative"
          label="Excluir"
          @click="$emit('excluir')"
        />
        <q-btn
          v-if="podeEditarOportunidade(oportunidade)"
          flat
          no-caps
          color="primary"
          label="Editar"
          @click="$emit('editar')"
        />
        <q-btn
          v-if="podeEditarOportunidade(oportunidade) && podeVerComissaoNegocios && comissaoVisivel(oportunidade)"
          unelevated
          no-caps
          color="primary"
          icon="payments"
          label="Registrar recebimento"
          @click="$emit('receber')"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import negociosMixin from "@/mixins/negocios";

const ICONES_DO_HISTORICO = {
  abertura: "flag",
  anotacao: "edit_note",
  valor_contratado: "monetization_on",
  alerta: "alarm",
  operador: "person",
  checklist: "fact_check",
  nota_fiscal: "receipt_long"
};

export default {
  name: "negocios-drawer-oportunidade",

  emits: ["alterar-data", "editar", "editar-recebimento", "emitir-nota-fiscal", "excluir", "update:modelValue", "marcar-checklist", "mover", "receber"],
  mixins: [negociosMixin],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      textoAnotacao: "",
      erroAnotacao: "",
      anotacoesEmEnvio: {}
    };
  },
  computed: {
    ...mapGetters(["negociosEtapas", "negociosEmiteNotaFiscal"]),
    historicoRecentePrimeiro() {
      return [...(this.oportunidade.historico || [])].sort((a, b) => b.id - a.id);
    },
    salvandoAnotacao() {
      return !!this.oportunidade && !!this.anotacoesEmEnvio[this.oportunidade.id];
    },
    oportunidade() {
      return this.$store.getters.negociosOportunidade;
    },
    checklist() {
      return this.oportunidade && this.oportunidade.checklist ? this.oportunidade.checklist : null;
    },
    podeAlterarChecklist() {
      return Boolean(this.checklist) && this.podeEditarNegocios && !this.checklist.somente_leitura;
    },
    listaDeLinhas() {
      return this.oportunidade ? this.linhasDaOportunidade(this.oportunidade) : [];
    },
    nomeDoCliente() {
      return this.oportunidade.cliente ? this.oportunidade.cliente.nome : "Cliente removido";
    },
    subtitulo() {
      const documento = this.oportunidade.cliente ? this.oportunidade.cliente.cpfcnpj : null;
      const linha = this.oportunidade.linha ? this.oportunidade.linha.nome : "";

      return documento ? documento + " · " + linha : linha;
    },
    alerta() {
      return this.rotuloDoAlerta(this.oportunidade.data_alerta);
    },
    etapaAtual() {
      return this.negociosEtapas.find(etapa => etapa.id === this.oportunidade.etapa_id) || null;
    },
    nomeDaEtapa() {
      if (!this.oportunidade.etapa) {
        return "—";
      }

      return this.oportunidade.etapa.nome + (this.oportunidade.etapa.encerra ? " · final" : "");
    },
    avisoDoPrazo() {
      const etapa = this.etapaAtual;

      if (!etapa || etapa.dias_alerta === null) {
        return "Esta etapa não tem prazo padrão";
      }

      const dias = etapa.dias_alerta === 1 ? "1 dia" : etapa.dias_alerta + " dias";

      return "Prazo padrão desta etapa: " + dias;
    }
  },
  watch: {
    "oportunidade.id"() {
      this.textoAnotacao = "";
      this.erroAnotacao = "";
    }
  },
  methods: {
    async registrarAnotacao() {
      const oportunidade = this.oportunidade;
      const texto = this.textoAnotacao.trim();

      if (this.salvandoAnotacao || !this.podeEditarOportunidade(oportunidade)) {
        return;
      }

      if (!texto) {
        this.erroAnotacao = "Escreva a anotação antes de registrar.";
        return;
      }

      const id = oportunidade.id;
      this.erroAnotacao = "";
      this.anotacoesEmEnvio = { ...this.anotacoesEmEnvio, [id]: true };

      let resposta;

      try {
        resposta = await this.$store.dispatch("addNegociosAnotacao", { id, texto });
      } catch (erro) {
        resposta = { success: false, message: "Não foi possível registrar a anotação. Tente novamente." };
      }

      const emEnvio = { ...this.anotacoesEmEnvio };
      delete emEnvio[id];
      this.anotacoesEmEnvio = emEnvio;

      if (!this.oportunidade || this.oportunidade.id !== id) {
        return;
      }

      if (resposta && resposta.success) {
        this.textoAnotacao = "";
        return;
      }

      this.erroAnotacao = (resposta && resposta.message) || "Não foi possível registrar a anotação.";
    },
    iconeDoRegistro(registro) {
      return ICONES_DO_HISTORICO[registro.tipo] || "swap_horiz";
    },
    tituloDoRegistro(registro) {
      if (registro.tipo === "abertura") {
        return "Oportunidade aberta";
      }

      if (registro.tipo === "valor_contratado") {
        const anterior = registro.valor_anterior === null ? "—" : this.formatarMoeda(registro.valor_anterior);

        return "Valor contratado: " + anterior + " → " + this.formatarMoeda(registro.valor_novo);
      }

      if (registro.tipo === "alerta") {
        return "Retorno vencido";
      }

      if (registro.tipo === "operador") {
        return "Operador transferido";
      }

      if (registro.tipo === "checklist") {
        return registro.checklist_marcado ? "Documento conferido" : "Documento desmarcado";
      }

      if (registro.tipo === "anotacao") {
        return "Anotação";
      }

      if (registro.tipo === "nota_fiscal") {
        return "Nota fiscal emitida";
      }

      if (!registro.etapa_origem_id && !registro.etapa_destino_id) {
        return "Movimentação da proposta";
      }

      return this.nomeDeEtapa(registro.etapa_origem_id) + " → " + this.nomeDeEtapa(registro.etapa_destino_id);
    },
    nomeDeEtapa(etapaId) {
      const etapa = this.negociosEtapas.find(item => item.id === etapaId);

      return etapa ? etapa.nome : "Etapa removida";
    },
    detalheDaLinha(linha) {
      const partes = [];

      if (linha.categoria) {
        partes.push(linha.categoria);
      }

      if (this.comissaoVisivel(linha) && linha.percentual_previsto !== null && linha.percentual_previsto !== undefined) {
        partes.push(this.formatarPercentual(linha.percentual_previsto) + " de comissão");
      }

      return partes.join(" · ");
    },
    podeEmitirNotaFiscal(recebimento) {
      return this.podeVerComissaoNegocios && this.negociosEmiteNotaFiscal && !recebimento.nota_fiscal_id;
    },
    alternarChecklistItem(item) {
      if (!this.podeAlterarChecklist) {
        return;
      }

      this.$emit("marcar-checklist", { item: item.item, marcado: !item.marcado });
    }
  }
};
</script>
